import { type SuretyAccountId } from "@inrev/common";
import {
	useFetchAccountIndividuals,
	useFetchAccountLatestIndemnityAgreement,
	useFetchAccountLatestWipStatement,
	useFetchAccountMetadata,
	useFetchSuretyAccount,
} from "@portal/api/account";
import { useFetchFinancialExtractions, useFetchFinancialPeriods } from "@portal/api/financials";
import { useShowAccountContractHistory } from "@portal/hooks/useShowAccountContractHistory";
import { useMemo } from "react";
import { computeActionItems } from "./computeActionItems";
import type { UseActionItemsResult } from "./types";

export type { UseActionItemsResult } from "./types";

export function useActionItems(accountId: SuretyAccountId): UseActionItemsResult {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError } =
		useFetchSuretyAccount(accountId);
	const { metadata, metadataIsLoading, metadataError } = useFetchAccountMetadata(accountId);
	const { individuals, individualsIsLoading, individualsError } =
		useFetchAccountIndividuals(accountId);
	const { latestWipStatement, latestWipStatementIsLoading, latestWipStatementError } =
		useFetchAccountLatestWipStatement(accountId);
	const {
		latestIndemnityAgreement,
		latestIndemnityAgreementIsLoading,
		latestIndemnityAgreementError,
	} = useFetchAccountLatestIndemnityAgreement(accountId);
	const { financialPeriods, financialPeriodsLoading, financialPeriodsError } =
		useFetchFinancialPeriods(accountId);
	const { financialExtractions, financialExtractionsLoading, financialExtractionsError } =
		useFetchFinancialExtractions(accountId);
	const showContractHistory = useShowAccountContractHistory(
		suretyAccount?.isContractSuretyAccount ?? true,
	);

	return useMemo((): UseActionItemsResult => {
		const isLoading =
			suretyAccountIsLoading ||
			individualsIsLoading ||
			metadataIsLoading ||
			latestWipStatementIsLoading ||
			latestIndemnityAgreementIsLoading ||
			financialPeriodsLoading ||
			financialExtractionsLoading;

		if (isLoading) {
			return { items: [], isLoading: true, isError: false };
		}

		const isError =
			!!suretyAccountError ||
			!suretyAccount ||
			!!individualsError ||
			!individuals ||
			!!metadataError ||
			!metadata ||
			!!latestWipStatementError ||
			!!latestIndemnityAgreementError ||
			!!financialPeriodsError ||
			!!financialExtractionsError;

		if (isError) {
			return { items: [], isLoading: false, isError: true };
		}

		return {
			items: computeActionItems({
				account: {
					status: suretyAccount.status,
					modelSize: suretyAccount.modelSize,
					history: metadata,
					isContractSuretyAccount: suretyAccount.isContractSuretyAccount,
				},
				individuals,
				latestWipStatement,
				latestIndemnityAgreement,
				financialPeriods,
				financialExtractions,
				showContractHistory,
			}),
			isLoading: false,
			isError: false,
		};
	}, [
		suretyAccount,
		suretyAccountIsLoading,
		suretyAccountError,
		metadata,
		metadataIsLoading,
		metadataError,
		individuals,
		individualsIsLoading,
		individualsError,
		latestWipStatement,
		latestWipStatementIsLoading,
		latestWipStatementError,
		latestIndemnityAgreement,
		latestIndemnityAgreementIsLoading,
		latestIndemnityAgreementError,
		financialPeriods,
		financialPeriodsLoading,
		financialPeriodsError,
		financialExtractions,
		financialExtractionsLoading,
		financialExtractionsError,
		showContractHistory,
	]);
}
